import type { Schemas } from "@norn/sdk"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { CircleCheck, CircleX, Rocket } from "lucide-react"
import { type ReactNode, useRef, useState } from "react"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { run, scopeQuery } from "@/lib/api"
import { parseLosslessSet, serialize } from "@/lib/candidate"
import { preflight } from "@/lib/checker"
import type { Scope } from "@/lib/connection"
import { useT } from "@/lib/i18n"
import { parseExact, stringifyExact } from "@/lib/lossless"
import { useCell } from "@/lib/session"
import { shortHash } from "@/lib/utils"
import { RefusalNote } from "@/workspace/values"
import { ORDER_SYNC, type PromoteCounts, type SyncSpec, artifactSha256, syncInput } from "./promote"

export const PROMOTE_ACTION = "recon.order_sync.promote"

/** One sync to promote and the counts it is promoted with. */
export interface PromotedSync {
  readonly spec: SyncSpec
  readonly counts: PromoteCounts
}

/**
 * Promote a workbook's match: show each SDK-generated MappingIR, check them together against this
 * cell's installed definitions with the in-browser Rust checker, then record each promotion as an
 * authorized action. Installing a mapping stays an administrator's definitions change; the record
 * says "proposed".
 */
export function PromoteDialog({
  scope,
  actor,
  syncs,
  rules,
  ready,
  onClose,
  title,
  description,
  footnote,
}: {
  scope: Scope
  actor: string | null
  syncs: readonly PromotedSync[]
  rules: string
  ready: boolean
  onClose: () => void
  title: string
  description: string
  footnote: ReactNode
}) {
  const t = useT()
  const { connection, client } = useCell()
  const queryClient = useQueryClient()
  // One key per sync per dialog: a retried submit is an exact replay, never a second promotion.
  const [idempotencyKeys] = useState(() => syncs.map(() => crypto.randomUUID()))
  // The requests are frozen at the first submit, so a retry replays them exactly even if counts refresh.
  const frozen = useRef<string[] | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const ids = syncs.map((s) => String(s.spec.mapping.artifactId))
  const check = useQuery({
    queryKey: ["wb-promote-check", connection.baseUrl, scope.orgId, scope.environmentId, ...ids],
    retry: false,
    staleTime: 0,
    queryFn: async () => {
      const text = await run(connection.baseUrl, () => client.GET("/v1/defs", { params: { query: scopeQuery(scope) }, parseAs: "text" }))
      const set = parseLosslessSet(text)
      const candidate = [...set.candidate.filter((a) => !ids.includes(String(a.artifactId))), ...syncs.map((s) => s.spec.mapping)]
      const [outcome, hashes] = await Promise.all([preflight(serialize(candidate), serialize(set.base)), Promise.all(syncs.map((s) => artifactSha256(s.spec.mapping)))])
      return { outcome, hashes }
    },
  })
  const promote = useMutation({
    mutationFn: async () => {
      // A first submission needs complete reads; a retry replays the frozen requests regardless.
      if (!actor || !check.data || (!frozen.current && !ready)) throw new Error("Not ready")
      const data = check.data
      frozen.current ??= syncs.map((s, i) =>
        stringifyExact({
          scope: { orgId: scope.orgId, environmentId: scope.environmentId },
          actor,
          input: syncInput(s.spec, data.hashes[i] ?? "", data.outcome.admitted, s.counts, rules),
          idempotencyKey: idempotencyKeys[i],
        }),
      )
      const receipts: Schemas["ActionAccepted"][] = []
      for (const body of frozen.current) {
        const text = await run(connection.baseUrl, () =>
          client.POST("/v1/actions/{action}", { params: { path: { action: PROMOTE_ACTION } }, body: {} as never, bodySerializer: () => body, parseAs: "text" }),
        )
        receipts.push(parseExact(text) as Schemas["ActionAccepted"])
      }
      return receipts
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["wb-all"] }),
  })
  const done = promote.isSuccess
  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open && !promote.isPending) onClose()
      }}
      size="lg"
      icon={<Rocket className="size-5" aria-hidden />}
      title={title}
      description={description}
      confirmLabel={done ? t("wb.promote.done") : t("wb.promote.confirm")}
      pendingLabel={t("wb.promote.pending")}
      cancelLabel={t("common.cancel")}
      pending={promote.isPending}
      confirmDisabled={!check.data?.outcome.admitted || !actor || (!ready && !submitted)}
      onConfirm={() => {
        if (done) return onClose()
        setSubmitted(true)
        promote.mutate()
      }}
    >
      <div className="mt-4 max-h-[55vh] space-y-4 overflow-y-auto pe-1 text-sm">
        {syncs.map(({ spec }, i) => {
          const payload = spec.mapping.payload as { maps: { id: string; source: { dataset: string }; target: { entity: string }; properties: { property: string; value: string }[] }[] }
          const sha256 = check.data?.hashes[i]
          return (
            <div key={spec.syncId} className="space-y-1.5 rounded-xl border border-border p-3">
              <p className="flex flex-wrap items-baseline gap-x-2">
                <span dir="ltr" className="font-mono text-[13px] font-medium">
                  {String(spec.mapping.artifactId)}@{String(spec.mapping.version)}
                </span>
                <span className="text-xs text-muted-foreground">MappingIR</span>
                {sha256 ? (
                  <span dir="ltr" className="font-mono text-2xs text-subtle-foreground" title={sha256}>
                    sha256 {shortHash(sha256)}
                  </span>
                ) : null}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("wb.promote.generated")}{" "}
                <span dir="ltr" className="font-mono text-2xs">
                  {spec.source}
                </span>
              </p>
              {payload.maps.map((m) => (
                <div key={m.id} className="space-y-1 pt-1">
                  <p className="text-xs">{t("wb.promote.maps", { system: spec.sourceSystem, dataset: m.source.dataset, target: m.target.entity.replace(/\.entity$/, "") })}</p>
                  <ul dir="ltr" className="space-y-0.5 font-mono text-2xs text-muted-foreground">
                    {m.properties.map((p) => (
                      <li key={p.property} className="truncate">
                        {p.property} ← {p.value}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )
        })}
        <div className="space-y-1">
          <p className="text-xs font-medium">{t("wb.promote.check")}</p>
          {check.isPending ? (
            <p className="text-xs text-muted-foreground">{t("wb.promote.checking")}</p>
          ) : check.isError ? (
            <RefusalNote error={check.error} title={t("wb.promote.checkFailed")} />
          ) : check.data.outcome.admitted ? (
            <p className="flex items-center gap-1.5 text-xs text-success">
              <CircleCheck className="size-3.5" aria-hidden /> {t("wb.promote.admitted")}
            </p>
          ) : (
            <div className="space-y-1">
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <CircleX className="size-3.5" aria-hidden /> {t("wb.promote.refused")}
              </p>
              <ul dir="ltr" className="space-y-0.5 font-mono text-2xs text-muted-foreground">
                {check.data.outcome.diagnostics.slice(0, 6).map((d, i) => (
                  <li key={`${d.code}-${i}`}>
                    {d.code}: {d.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {syncs.map(({ spec, counts }) => (
          <p key={spec.syncId} className="text-xs text-muted-foreground">
            {syncs.length > 1 ? <span className="font-medium text-foreground">{spec.sourceSystem} · </span> : null}
            {t("wb.promote.records", { matched: counts.matched, review: counts.needsReview, unmatched: counts.unmatched })}
          </p>
        ))}
        <p className="rounded-lg bg-surface-subtle px-3 py-2 text-xs text-muted-foreground">{footnote}</p>
        {done ? (
          <p role="status" className="text-xs text-success">
            {t("wb.promote.recorded", { seq: promote.data.map((r) => String(r.receipt.seq)).join(", #") })}{" "}
            <Link to="/$orgId/$environmentId/data/sources" params={scope} search={{}} className="underline underline-offset-4">
              {t("wb.promote.seeSources")}
            </Link>
          </p>
        ) : null}
        {promote.isError ? <RefusalNote error={promote.error} title={t("wb.promote.notRecorded")} /> : null}
      </div>
    </ConfirmDialog>
  )
}

/** The order workbook's promotion: its one sync. */
export function OrderPromoteDialog({ scope, actor, counts, rules, ready, onClose }: { scope: Scope; actor: string | null; counts: PromoteCounts; rules: string; ready: boolean; onClose: () => void }) {
  const t = useT()
  return (
    <PromoteDialog
      scope={scope}
      actor={actor}
      syncs={[{ spec: ORDER_SYNC, counts }]}
      rules={rules}
      ready={ready}
      onClose={onClose}
      title={t("wb.promote.title")}
      description={t("wb.promote.description")}
      footnote={t("wb.promote.install")}
    />
  )
}
